import { diffInYearsFromNow } from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import isFuture from 'date-fns/isFuture';
import { Link } from 'expo-router';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { KeyboardAvoidingView } from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledDateTextInput,
    ControlledInput,
    ControlledPhoneNumberInput,
    ControlledSSNInput,
    Form,
    USE_FORM_MODE_DEFAULTS,
    useThemeColors,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { POLICY_DISCLOSURES, PolicyType } from '../Disclosures';

const schema = z.object({
    legalFirstName: z.string().min(1, 'Legal first name is required'),
    legalLastName: z.string().min(1, 'Legal last name is required'),
    dateOfBirth: z
        .string()
        .min(10, 'Date of birth is required')
        .regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Use MM/DD/YYYY format')
        .refine(
            (dateOfBirth) => {
                const diff = diffInYearsFromNow(dateOfBirth);
                return (
                    diff <= 120 && diff >= 0 && !isFuture(new Date(dateOfBirth))
                );
            },
            {
                message: 'Please enter a valid date of birth',
            },
        ),
    taxId: z.string().min(9, 'Tax ID is required'),
    phoneNumber: z.string().min(10, 'Phone number is required'),
    email: z.string().email('Enter a valid email address'),
});

export type BeneficiaryFormValues = z.infer<typeof schema>;

interface AddBeneficiaryFormProps {
    onContinue: (data: BeneficiaryFormValues) => void;
}

export const AddBeneficiaryForm = ({ onContinue }: AddBeneficiaryFormProps) => {
    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<BeneficiaryFormValues>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(schema),
        defaultValues: {
            legalFirstName: '',
            legalLastName: '',
            dateOfBirth: '',
            taxId: '',
            phoneNumber: '',
            email: '',
        },
    });

    const { getColor } = useThemeColors();

    const onSubmit: SubmitHandler<BeneficiaryFormValues> = (data) => {
        onContinue(data);
    };

    return (
        <KeyboardAvoidingView behavior="padding" keyboardVerticalOffset={120}>
            <YStack gap={24} paddingVertical={24}>
                <BodyMediumRegular color="$gray1000">
                    Beneficiaries are the individual(s) you designate to receive
                    the funds in your accounts after the last surviving account
                    owner has passed away. Please see your{' '}
                    <Link
                        href={
                            POLICY_DISCLOSURES[
                                PolicyType.DEPOSIT_ACCOUNT_AGREEMENT
                            ].url
                        }
                        style={{
                            color: getColor('$gray1000'),
                        }}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <BodyMediumRegular textDecorationLine="underline">
                            Fruitful Deposit Account Agreement
                        </BodyMediumRegular>
                    </Link>{' '}
                    for more information.
                </BodyMediumRegular>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <YStack gap={16}>
                        <ControlledInput
                            name="legalFirstName"
                            label="Legal first name"
                            placeholder="First name"
                            control={control}
                            register={register}
                            errors={errors}
                            autoCapitalize="words"
                            autoComplete="name"
                        />
                        <ControlledInput
                            name="legalLastName"
                            label="Legal last name"
                            placeholder="Last name"
                            control={control}
                            register={register}
                            errors={errors}
                            autoCapitalize="words"
                            autoComplete="name"
                        />
                        <ControlledDateTextInput
                            name="dateOfBirth"
                            label="Date of birth"
                            control={control}
                            register={register}
                            errors={errors}
                        />
                        <ControlledSSNInput
                            name="taxId"
                            label="Tax Identification Number (SSN or ITIN)"
                            control={control}
                            register={register}
                            errors={errors}
                            allowToggle
                            storeUnmasked
                        />
                        <ControlledPhoneNumberInput
                            name="phoneNumber"
                            label="Phone number"
                            control={control}
                            register={register}
                            errors={errors}
                        />
                        <ControlledInput
                            name="email"
                            label="Email address"
                            placeholder="Beneficiary's email address"
                            control={control}
                            register={register}
                            errors={errors}
                            keyboardType="email-address"
                            autoCapitalize="none"
                            autoComplete="email"
                        />
                    </YStack>
                </Form>
                <Button
                    text="Continue"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    disabled={!isValid}
                    onPress={handleSubmit(onSubmit)}
                />
            </YStack>
        </KeyboardAvoidingView>
    );
};
